import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { useMemo, useState } from "react";
import type { Database } from "../types/gen/database.types";
import { projectKey, projectURL } from "./supabaseConstants";
import {
  getCurrentStateOfDischarge,
  getCurrentStateOfNotes,
  getCurrentStateOfPhone,
  getCurrentStateOfPreHistory,
  removeDischargeSubscriptions,
  removeNotesSubscriptions,
  removePhoneSubscriptions,
  removePreHistorySubscriptions,
  removeSubscriptions,
  subscribeToDischargeStream,
  subscribeToNotesStream,
  subscribeToPhoneStream,
  subscribeToPreHistoryStream,
  uploadToSupabaseSignedURL,
} from "./supabaseUtils";
import { type SupabaseContextType } from "./supabaseTypes";

type GetToken = (args: { template: string }) => Promise<string | null>;

const supabaseContext: {
  accessToken: () => Promise<string | null>;
  client: SupabaseClient<Database> | undefined;
} = {
  accessToken: () => {
    throw new Error("");
  },
  client: undefined,
};

interface UseSupabaseContextArgs {
  useAuth: () => { getToken: GetToken };
  // useEffect: typeof useEffect;
  useEffect: (effect: () => void, deps?: unknown[]) => void;
  useMemo: typeof useMemo;
  useState: typeof useState;
}

interface UseSupabaseContextReturn {
  supabase: SupabaseClient<Database>;
  context: SupabaseContextType;
}

export function useSupabaseContext({
  useAuth,
  useEffect,
  useMemo,
  useState,
}: UseSupabaseContextArgs): UseSupabaseContextReturn {
  const { getToken } = useAuth();

  useEffect(() => {
    supabaseContext.accessToken = () => getToken({ template: "supabase" });
  }, [getToken]);

  const [supabase] = useState<SupabaseClient<Database>>(() => {
    supabaseContext.accessToken = () => getToken({ template: "supabase" });
    if (!supabaseContext.client) {
      supabaseContext.client = createClient<Database>(projectURL, projectKey, {
        accessToken: () => supabaseContext.accessToken(),
      });
    }
    return supabaseContext.client;
  });

  const context: SupabaseContextType = useMemo(
    () => ({
      uploadToSupabaseSignedURL: uploadToSupabaseSignedURL(supabase),
      getCurrentStateOfDischarge: getCurrentStateOfDischarge(supabase),
      getCurrentStateOfNotes: getCurrentStateOfNotes(supabase),
      getCurrentStateOfPhone: getCurrentStateOfPhone(supabase),
      getCurrentStateOfPreHistory: getCurrentStateOfPreHistory(supabase),
      subscribeToDischargeStream: subscribeToDischargeStream(supabase),
      subscribeToNotesStream: subscribeToNotesStream(supabase),
      subscribeToPreHistoryStream: subscribeToPreHistoryStream(supabase),
      subscribeToPhoneStream: subscribeToPhoneStream(supabase),
      removeDischargeSubscriptions: removeDischargeSubscriptions(supabase),
      removeNotesSubscriptions: removeNotesSubscriptions(supabase),
      removePhoneSubscriptions: removePhoneSubscriptions(supabase),
      removePreHistorySubscriptions: removePreHistorySubscriptions(supabase),
      removeSubscriptions: removeSubscriptions(supabase),
    }),
    [supabase]
  );

  return { supabase, context };
}
